<template>
  <div class="table-section">
    <div v-if="status.isErrored">
      <div class="empty-section">
        <slot name="error">
          <ErrorState />
        </slot>
      </div>
    </div>
    <div v-else-if="status.isLoaded && numberOfItems === 0">
      <div class="empty-section">
        <slot name="empty">
          <EmptyState
            :image="emptyImage"
            :boldMessage="emptyBoldMessage"
            :message="getEmptyMessage()"
            :subMessage="emptySubMessage"
            :actions="emptyActions"
            :help="emptyHelp"
          />
        </slot>
      </div>
    </div>
    <div v-else>
      <div class="desktop">
        <div class="table-section-header">
          <div class="flex-fill">{{ name }}</div>
          <ActionIcons :actions="headerActions" class="mr-3" />
          <div v-if="pagination" class="d-flex">
            <TablePagination
              v-if="pages > 1"
              :pagination="pagination"
              :max-pages="maxPages"
              :pages="pages"
              v-on:update:pagination="updatePagination"
              class="pr-2"
            />
            <TableSize
              :name="name"
              :pagination="pagination"
              :size-options="sizeOptions"
              v-on:update:pagination="updatePagination"
            />
          </div>
        </div>
        <div v-if="$slots.content" class="table-content">
          <slot name="content" />
        </div>
        <table class="table">
          <thead>
            <tr>
              <th v-for="(header, index) in headers" v-bind:key="index" scope="col">
                <div class="d-flex" :class="header.display.style">
                  <div
                    v-if="header.sortProperty"
                    v-on:click="onSortClick(header)"
                    class="d-flex align-items-center sortable"
                  >
                    <span
                      v-if="header.sortProperty"
                      :class="{ selected: isSelected(header.display.value) }"
                    >
                      {{ header.display.value }}
                    </span>
                    <Icon
                      :size="14"
                      class="rotating"
                      :rotate="getSortIconRotation()"
                      icon="sort-arrow"
                      :color="sort ? 'epsilon' : 'lambda'"
                    />
                  </div>
                  <span v-else>{{ header.display.value }}</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-if="status.isLoading">
              <tr>
                <td v-for="index in headers.length" v-bind:key="index">
                  <div class="loading" />
                </td>
              </tr>
            </template>
            <slot v-else name="desktop" />
          </tbody>
        </table>
      </div>
      <div class="mobile">
        <div class="table-section-header flex-column">
          <div class="d-flex">
            <div class="flex-fill">{{ name }}</div>
            <ActionIcons :actions="headerActions" />
          </div>
          <div v-if="pagination" class="d-flex pt-3">
            <div v-if="pages > 1" class="d-flex flex-column justify-content-start">
              <TablePage
                :name="name"
                :pagination="pagination"
                :pages="pages"
                v-on:update:pagination="updatePagination"
              />
            </div>
            <div class="flex-fill d-flex justify-content-end">
              <div class="d-flex flex-column">
                <TableSize
                  :name="name"
                  :pagination="pagination"
                  :size-options="sizeOptions"
                  v-on:update:pagination="updatePagination"
                  class="d-flex justify-content-end"
                />
                <TableSort
                  :name="name"
                  :headers="headers"
                  :sort="sort"
                  v-on:update:sort="updateSort"
                  v-on:update:selected="updateSelected"
                  class="d-none pt-2"
                />
              </div>
            </div>
          </div>
        </div>
        <div v-if="status.isLoading" class="loading-wrapper">
          <div class="loading w-100" />
        </div>
        <template v-else>
          <div v-if="$slots.content" class="table-content">
            <slot name="content" />
          </div>
          <slot name="mobile" />
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref } from "@vue/composition-api";
import ActionIcons from "@/components/display/ActionIcons";
import EmptyState from "@/components/display/empty-state/EmptyState";
import ErrorState from "@/components/display/ErrorState";
import Icon from "@/components/display/icon/Icon";
import Status from "@/classes/Status";
import TablePage from "@/components/display/table/TablePage";
import TablePagination from "@/components/display/table/TablePagination";
import TableSize from "@/components/display/table/TableSize";
import TableSort from "@/components/display/table/TableSort";

export default {
  name: "TableComp",
  props: {
    emptyBoldMessage: String,
    emptyImage: String,
    emptyMessage: String,
    emptySubMessage: String,
    emptyActions: Array,
    emptyHelp: Array,
    headers: {
      type: Array,
      required: true,
    },
    numberOfItems: {
      type: Number,
      required: true,
    },
    maxPages: Number,
    name: String,
    pagination: Object,
    sizeOptions: Array,
    sort: Object,
    status: {
      type: [Object, Status],
      required: true,
    },
    headerActions: {
      type: Array,
      default: () => [],
    },
  },
  components: {
    ActionIcons,
    EmptyState,
    ErrorState,
    Icon,
    TablePage,
    TablePagination,
    TableSize,
    TableSort,
  },
  setup(props, { emit, root }) {
    const selected = ref(null);
    const isSelected = (headerValue) => selected.value === headerValue;
    const pages = computed(() => Math.ceil(props.pagination?.total / props.pagination?.size));
    const updateList = (pagination, sort) => {
      emit("update:list", { pagination, sort });
    };
    const updateSort = (sort) => updateList(props.pagination, sort);
    const updateSelected = (value) => (selected.value = value);
    const onSortClick = (header) => {
      const rotate = (direction) => {
        switch (direction) {
          case "asc":
            return "desc";
          case "desc":
            return null;
          default:
            return "asc";
        }
      };
      const direction = rotate(
        props.sort?.property !== header.sortProperty ? null : props.sort?.direction
      );
      const property = direction
        ? props.sort?.property !== header.sortProperty
          ? header.sortProperty
          : props.sort?.property
        : null;
      updateSort({ property, direction });
      updateSelected(direction ? header.display.value : null);
    };
    const updatePagination = (event) => updateList(event, props.sort);
    const getSortIconRotation = () => (props.sort?.direction === "desc" ? 180 : 0);
    const getEmptyMessage = () =>
      props.emptyMessage ||
      root.$t("global.empty-table-message", { resourcesType: props.name.toLowerCase() });

    const iconAction = (icon) => {
      if (icon.action) {
        icon.action();
      }
    };

    return {
      getSortIconRotation,
      getEmptyMessage,
      iconAction,
      isSelected,
      onSortClick,
      pages,
      updateSort,
      updatePagination,
      updateSelected,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "~@/styles/variables.scss";
.table-section {
  border-radius: $border-radius;
  box-shadow: $card-box-shadow;
  .table-section-header {
    display: flex;
    min-height: 3rem;
    padding: 0.75rem 0.875rem;
    font-size: 1.25rem;
    border-bottom-style: solid;
    border-bottom-width: 1px;
  }
  .empty-section {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 8rem;
    font-size: 1.5rem;
  }
  .table-content {
    font-size: 0.875rem;
    padding: 0.75rem 0.875rem;
  }
  .mobile {
    .empty-section {
      font-size: 1rem;
      padding: 2rem;
    }
  }

  .sortable {
    cursor: pointer;
    &:hover {
      font-weight: 600;
    }
    .selected {
      font-weight: bold;
    }
  }
  table {
    tr {
      td {
        vertical-align: middle;
        &:first-of-type {
          .loading {
            margin-left: 0;
          }
        }
      }
    }
  }
  .loading {
    height: 0.75rem;
    margin: 0 5px;
  }
  .mobile {
    .loading-wrapper {
      display: flex;
      align-items: center;
      min-height: 3rem;
      border-top-width: 1px;
      border-top-style: solid;
      padding: 0 0.75rem;
    }
  }
}
</style>
